import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BiddingCriteriaSection = ({ criteria, onUpdate, isEditing, onToggleEdit }) => {
  const [formData, setFormData] = useState(criteria);

  const trafficSources = [
    { value: 'social', label: 'Social Media' },
    { value: 'tv', label: 'Television' },
    { value: 'radio', label: 'Radio' },
    { value: 'direct_mail', label: 'Direct Mail' },
    { value: 'search', label: 'Search Engine' },
    { value: 'video', label: 'Video Advertising' },
    { value: 'display', label: 'Display Advertising' },
    { value: 'email', label: 'Email Marketing' }
  ];

  const insuranceTypes = [
    { value: 'auto', label: 'Auto Insurance' },
    { value: 'home', label: 'Home Insurance' },
    { value: 'life', label: 'Life Insurance' },
    { value: 'health', label: 'Health Insurance' },
    { value: 'commercial', label: 'Commercial Insurance' },
    { value: 'renters', label: 'Renters Insurance' },
    { value: 'motorcycle', label: 'Motorcycle Insurance' },
    { value: 'boat', label: 'Boat Insurance' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRangeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev?.[field],
        ...value
      }
    }));
  };

  const calculateProjectedCost = () => {
    const baseCost = formData?.maxBidAmount || 0;
    const dailyLeads = formData?.expectedDailyLeads || 0;
    return (baseCost * dailyLeads * 30)?.toFixed(2);
  };

  const handleSave = () => {
    onUpdate(formData);
    onToggleEdit();
  };

  const handleCancel = () => {
    setFormData(criteria);
    onToggleEdit();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Target" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Bidding Criteria</h3>
            <p className="text-sm text-muted-foreground">Configure your lead targeting and bidding parameters</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onToggleEdit} iconName="Edit2">
              Edit Criteria
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-6">
        {/* Insurance Types */}
        <div>
          <Select
            label="Insurance Types"
            description="Select the types of insurance leads you want to bid on"
            options={insuranceTypes}
            value={formData?.insuranceTypes}
            onChange={(value) => handleInputChange('insuranceTypes', value)}
            disabled={!isEditing}
            multiple
            searchable
            required
          />
        </div>

        {/* Traffic Sources */}
        <div>
          <Select
            label="Preferred Traffic Sources"
            description="Choose the marketing channels you prefer for lead generation"
            options={trafficSources}
            value={formData?.trafficSources}
            onChange={(value) => handleInputChange('trafficSources', value)}
            disabled={!isEditing}
            multiple
            searchable
          />
        </div>

        {/* Age Demographics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Minimum Age"
            type="number"
            value={formData?.ageRange?.min || ''}
            onChange={(e) => handleRangeChange('ageRange', { min: parseInt(e?.target?.value) })}
            disabled={!isEditing}
            min="18"
            max="100"
          />
          <Input
            label="Maximum Age"
            type="number"
            value={formData?.ageRange?.max || ''}
            onChange={(e) => handleRangeChange('ageRange', { max: parseInt(e?.target?.value) })}
            disabled={!isEditing}
            min="18"
            max="100"
          />
        </div>

        {/* Bidding Parameters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Maximum Bid Amount ($)"
            type="number"
            value={formData?.maxBidAmount || ''}
            onChange={(e) => handleInputChange('maxBidAmount', parseFloat(e?.target?.value))}
            disabled={!isEditing}
            min="0.25"
            step="0.25"
            required
          />
          <Input
            label="Auto-Bid Increment ($)"
            type="number"
            value={formData?.autoBidIncrement || ''}
            onChange={(e) => handleInputChange('autoBidIncrement', parseFloat(e?.target?.value))}
            disabled={!isEditing}
            min="0.25"
            step="0.25"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Maximum Auto-Bids"
            type="number"
            value={formData?.maxAutoBids || ''}
            onChange={(e) => handleInputChange('maxAutoBids', parseInt(e?.target?.value))}
            disabled={!isEditing}
            min="1"
            max="4"
            required
          />
          <Input
            label="Expected Daily Leads"
            type="number"
            value={formData?.expectedDailyLeads || ''}
            onChange={(e) => handleInputChange('expectedDailyLeads', parseInt(e?.target?.value))}
            disabled={!isEditing}
            min="1"
          />
        </div>

        {/* Advanced Criteria */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground">Advanced Criteria</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Exclude Duplicate Leads"
              checked={formData?.excludeDuplicates || false}
              onChange={(e) => handleInputChange('excludeDuplicates', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Real-time Lead Verification"
              checked={formData?.realtimeVerification || false}
              onChange={(e) => handleInputChange('realtimeVerification', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Weekend Bidding"
              checked={formData?.weekendBidding || false}
              onChange={(e) => handleInputChange('weekendBidding', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Holiday Bidding"
              checked={formData?.holidayBidding || false}
              onChange={(e) => handleInputChange('holidayBidding', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Cost Projection */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-foreground">Monthly Cost Projection</h4>
              <p className="text-xs text-muted-foreground">Based on current settings</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-foreground">${calculateProjectedCost()}</p>
              <p className="text-xs text-muted-foreground">Estimated monthly spend</p>
            </div>
          </div>
          <div className="mt-3 flex items-center space-x-4 text-xs text-muted-foreground">
            <span>Max Bid: ${formData?.maxBidAmount || 0}</span>
            <span>•</span>
            <span>Daily Leads: {formData?.expectedDailyLeads || 0}</span>
            <span>•</span>
            <span>Auto-Increment: ${formData?.autoBidIncrement || 0.25}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiddingCriteriaSection;